import React, { FC } from "react";
import NcImage from "shared/NcImage/NcImage";
import ButtonSecondary from "../../shared/Button/ButtonSecondary";
import { IService } from "../../@core/interfaces/IService";
import ButtonFeaturedIcon from "../ButtonFeaturedIcon/ButtonFeaturedIcon";
import { useServices } from "../../hooks/collections/useServices";
import { useAlert } from "../../@shared/hooks/useAlert";
import { useUserSessionStore } from "../../stores/UserSession.store";
import { UserTypeEnum } from "../../@core/enums/UserType.enum";
import { ClockIcon } from "@heroicons/react/24/outline";
import schoonLogo from "images/schoon_transparent.svg";

export interface CarCardProps {
  className?: string;
  data?: IService | any;
  size?: "default" | "small";
  redirectToServiceForm?: (service?: IService) => void;
}

const CarCard: FC<CarCardProps> = ({
  size = "default",
  className = "",
  data,
  redirectToServiceForm
}) => {
  const alert = useAlert();
  const user = useUserSessionStore((state) => state.user);
  const { updateService } = useServices(false);
  const {
    title,
    details: { duration },
    images,
    price,
    id,
    featured
  } = data;

  const handleToggleFeatured = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateService(id, { featured: !data?.featured });
      alert.toastSuccess("Service updated successfully");
    } catch (error) {
      console.log(error);
      alert.toastError("Failed to update service");
    }
  };

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full rounded-2xl overflow-hidden">
        <div className="aspect-w-16 aspect-h-9 ">
          <NcImage
            containerClassName="flex items-center justify-center"
            className="w-full"
            src={images?.length ? images[0] : schoonLogo}
          />
        </div>
        {redirectToServiceForm && user && user.role === UserTypeEnum.COMPANY && (
          <ButtonFeaturedIcon
            onClick={handleToggleFeatured}
            isFeatured={!!featured}
            className="absolute left-3 top-3 z-[1]"
          />
        )}
        {/*{saleOff && <SaleOffBadge className="absolute left-3 top-3" />}*/}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={size === "default" ? "p-5  space-y-4" : "p-3  space-y-2"}>
        <div className="space-y-2">
          <div className="flex flex-col items-center space-x-2">
            <h2
              className={`capitalize ${
                size === "default"
                  ? "text-xl font-semibold"
                  : "text-base font-medium"
              }`}
            >
              <span className="line-clamp-1">{title}</span>
            </h2>
            <small>{data.details.location.address}</small>
          </div>
          {/*<div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2">*/}
          {/*  <span className="">{seats} seats</span>*/}
          {/*  <span>-</span>*/}
          {/*  <span className="">{gearshift} </span>*/}
          {/*</div>*/}
        </div>
        <div className="w-14  border-b border-neutral-100 dark:border-neutral-800"></div>
        <div className="flex justify-center items-center">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-base font-semibold">
              ${price}
              {` `}
              {size === "default" && (
                <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                  /service
                </span>
              )}
            </span>

            <div className={"opacity-40"}> -</div>
            {size === "default" && (
              <div className="flex items-center mt-1">
                <ClockIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400 mr-1"/>
                <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                  {duration} {duration > 1 ? "hrs" : "hr"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center items-center mt-4">
          {redirectToServiceForm ? (
            <ButtonSecondary onClick={redirectToServiceForm}>Edit</ButtonSecondary>
          ) : (
            <ButtonSecondary
              className={"w-full"}
              href={`/service-detail/${id}`}
            >Book</ButtonSecondary>
          )}
          {/*<StartRating reviewCount={reviewCount} point={reviewStart} />*/}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-CarCard group relative border border-neutral-200 dark:border-neutral-700 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow bg-white dark:bg-neutral-900 will-change-transform ${className}`}
      data-nc-id="CarCard"
    >
      {redirectToServiceForm ? (
        <div className="flex flex-col">
          {renderSliderGallery()}
          {renderContent()}
        </div>
      ) : (
        <div className="flex flex-col">
          {renderSliderGallery()}
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default CarCard;
